import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSElement,
  WSText
} from "@wingspanhq/fe-component-library";
import { IRequirementDefinitionResponse } from "@wingspanhq/payments/dist/interfaces";
import { RequirementDefinitionStatus } from "@wingspanhq/payments/dist/interfaces/requirement";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import React from "react";
import { ErrorContextKey } from "../../../services/platform";
import { DefaultActionDetails } from "../components/DefaultActionDetails";
import { useUpdateRequirementDefinition } from "../../../query/requirementDefinitions/mutation/useUpdateRequirementDefinition";

export interface ArchiveRequirementModalProps {
  requirementDefinition: IRequirementDefinitionResponse;
  engagementLength: number;
  onClose: () => void;
}

export const ArchiveRequirementModal: React.FC<
  ArchiveRequirementModalProps
> = ({ requirementDefinition, engagementLength, onClose }) => {
  const { openSnackbar } = useWSSnackbar();
  const [updateRequirementDefinition, updateRequirementDefinitionMeta] =
    useUpdateRequirementDefinition(
      requirementDefinition.requirementDefinitionId
    );

  const handleArchive = async () => {
    await updateRequirementDefinition(
      {
        status: RequirementDefinitionStatus.Inactive
      },
      {
        onSuccess: () => {
          openSnackbar({
            theme: "dark",
            type: "success",
            message: "Requirement archived"
          });
          onClose();
        }
      }
    );
  };

  return (
    <>
      <WSText.ParagraphSm mb="L" weight="book">
        Are you sure you want to archive this requirement?
      </WSText.ParagraphSm>
      <DefaultActionDetails
        requirementDefinition={requirementDefinition}
        engagementLength={engagementLength}
      />

      <WSAlert
        my="2XL"
        theme="info"
        icon="info-circle"
        size="M"
        title="Archiving requirement - what to expect:"
      >
        <WSElement as="ul" ml="L">
          <li>
            Historical data (e.g. requirement completion) will be retained if
            the requirement is restored and added to engagement types.
          </li>
        </WSElement>
      </WSAlert>

      <WSErrorMessage
        error={updateRequirementDefinitionMeta.error}
        mb="XL"
        contextKey={ErrorContextKey.Other}
      />

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Secondary"
          },
          {
            label: "Archive requirement",
            loading: updateRequirementDefinitionMeta.isLoading,
            onAsyncClick: handleArchive,
            kind: "Primary"
          }
        ]}
      />
    </>
  );
};

export const useArchiveRequirementModal = () => {
  return useWSModal(ArchiveRequirementModal, {
    title: "Archive requirement",
    size: "M"
  });
};
